import { LocalizedLink } from "@/i18n/language";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageHeader from "@/components/feature/PageHeader";
import BookingCompanyNotice from "@/components/feature/BookingCompanyNotice";
import GalleryGrid from "@/components/feature/GalleryGrid";
import {
  FloorPlanTabs,
  FloorPlanImage,
} from "@/components/feature/VillaFloorPlan";
import { img } from "@/lib/images";
import { usePriceMap } from "@/hooks/usePrices";
import { useGalleryCaptions } from "@/hooks/useGalleryCaptions";
import { frangipaniGalleryImages } from "./gallery-data";

export default function VillaFrangipaniPage() {
  const { i18n } = useTranslation();
  const isDe = i18n.language.startsWith("de");
  const { priceMap, money } = usePriceMap();
  const galleryImages = useGalleryCaptions("villa-frangipani", frangipaniGalleryImages);

  const [planOpen, setPlanOpen] = useState(false);

  const roomFeatures = isDe
    ? [
        "Klimaanlage & Ventilator im Zimmer",
        "großer Kleiderschrank",
        "Leselampen am Bett",
        "Moskitonetz",
        "kein TV im Zimmer",
        "täglich 2 Flaschen Mineralwasser kostenlos",
      ]
    : [
        "Air conditioning & ceiling fan",
        "Large wardrobe",
        "Reading lamps at the bed",
        "Mosquito net",
        "No TV in the room",
        "Daily 2 bottles of mineral water free",
      ];

  const bathFeatures = isDe
    ? [
        "großes Badezimmer mit WC & Dusche",
        "Handtücher, Toilettenartikel (Duschgel, Shampoo, Conditioner)",
        "Haarföhn",
      ]
    : [
        "Large bathroom with WC & shower",
        "Towels, toiletries (shower gel, shampoo, conditioner)",
        "Hair dryer",
      ];

  const terraceFeatures = isDe
    ? [
        "Große Rattanmöbel-Sitzecke",
        "Anrichte mit Kühlschrank",
        "Audioanlage für CD, DVD, USB, iPhone",
      ]
    : [
        "Large rattan seating area",
        "Sideboard with refrigerator",
        "Audio system for CD, DVD, USB, iPhone",
      ];

  return (
    <div className="bg-background-50 text-foreground-950 min-h-screen">
      <Navbar />
      <main>
        <PageHeader
          eyebrow={isDe ? "Villa Frangipani" : "Villa Frangipani"}
          title={isDe ? "Zwei Schlafzimmer" : "Two Bedrooms"}
          eyebrowSize="text-lg md:text-2xl"
          titleSize="text-lg md:text-xl"
          subtitle={
            isDe
              ? "Alle Details unserer zweitgrößten Villa"
              : "All details of our second largest villa"
          }
          image={img("villas/villa-frangipani/header-villafrangipani-01.jpg")}
          imageAlt="Villa Frangipani"
        />

        <section className="py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left column */}
            <div className="lg:col-span-2">
              {/* Gallery */}
              <div className="mb-8">
                <GalleryGrid images={galleryImages} />
              </div>

              {/* Parameters */}
              <div className="flex flex-wrap gap-3 mb-6">
                <div className="flex items-center gap-2 bg-background-100 px-4 py-2 rounded-full text-sm text-foreground-700">
                  <i className="ri-user-line text-accent-700"></i>
                  {isDe ? "Max 4 Personen" : "Max 4 persons"}
                </div>
                <div className="flex items-center gap-2 bg-background-100 px-4 py-2 rounded-full text-sm text-foreground-700">
                  <i className="ri-hotel-bed-line text-accent-700"></i>
                  {isDe
                    ? "1 King Size & 1 Queen Size Bett"
                    : "1 King Size & 1 Queen Size bed"}
                </div>
                <div className="flex items-center gap-2 bg-background-100 px-4 py-2 rounded-full text-sm text-foreground-700">
                  <i className="ri-ruler-line text-accent-700"></i>
                  {isDe ? "110 qm" : "110 sqm"}
                </div>
                <FloorPlanTabs
                  open={planOpen}
                  onToggle={() => setPlanOpen((v) => !v)}
                />
              </div>

              {/* Floor plan */}
              {planOpen && (
              <FloorPlanImage
                deImg={img("villas/villa-frangipani/floor-plan-villa-frangipani_ger.png")}
                enImg={img("villas/villa-frangipani/floor-plan-villa-frangipani_eng.png")}
                alt={isDe ? "Grundriss Villa Frangipani" : "Floor plan Villa Frangipani"}
              />
              )}

              {/* Description */}
              <p className="text-foreground-700 leading-relaxed mb-8 text-base">
                {isDe
                  ? "Die Villa verfügt über zwei große Zimmer, ein großes Bad und eine sehr große Terrasse. Ideal für vier Personen die sich ein großes Bad teilen oder für eine Familie. Es stehen alle Einrichtungen wie Garten, Pool und Speisepavillon zur Verfügung."
                  : "The villa features two large rooms, one large bathroom and a very large terrace. Ideal for four people sharing one large bathroom or for a family. All facilities such as garden, pool and dining pavilion are available."}
              </p>

              {/* Features */}
              <div className="space-y-8">
                <div>
                  <h3 className="font-heading text-lg text-foreground-950 mb-4 flex items-center gap-2">
                    <i className="ri-star-line text-accent-700"></i>
                    {isDe ? "ZIMMER" : "ROOM"}
                  </h3>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {roomFeatures.map((f) => (
                      <li
                        key={f}
                        className="flex items-start gap-2 text-sm text-foreground-700"
                      >
                        <i className="ri-check-line text-accent-700 mt-0.5 flex-shrink-0"></i>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h3 className="font-heading text-lg text-foreground-950 mb-4 flex items-center gap-2">
                    <i className="ri-drop-line text-accent-700"></i>
                    {isDe ? "BADEZIMMER" : "BATHROOM"}
                  </h3>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {bathFeatures.map((f) => (
                      <li
                        key={f}
                        className="flex items-start gap-2 text-sm text-foreground-700"
                      >
                        <i className="ri-check-line text-accent-700 mt-0.5 flex-shrink-0"></i>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h3 className="font-heading text-lg text-foreground-950 mb-4 flex items-center gap-2">
                    <i className="ri-sun-line text-accent-700"></i>
                    {isDe ? "TERRASSE" : "TERRACE"}
                  </h3>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {terraceFeatures.map((f) => (
                      <li
                        key={f}
                        className="flex items-start gap-2 text-sm text-foreground-700"
                      >
                        <i className="ri-check-line text-accent-700 mt-0.5 flex-shrink-0"></i>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Right column */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Price box */}
                <div className="bg-background-50 border border-background-200 rounded-lg p-6">
                  <div className="text-xs text-foreground-500 uppercase tracking-wider mb-1">
                    {isDe ? "Preis" : "Price"}
                  </div>
                  <div className="text-3xl font-heading text-foreground-950 mb-1">
                    {money(priceMap["villa_frangipani_1"])}
                  </div>
                  <div className="text-sm text-foreground-600 mb-4">
                    {isDe ? "1 Zimmer / Nacht · inklusive Frühstück" : "1 room / night · incl. breakfast"}
                  </div>
                  <div className="text-3xl font-heading text-foreground-950 mb-1">
                    {money(priceMap["villa_frangipani_2"])}
                  </div>
                  <div className="text-sm text-foreground-600 mb-6">
                    {isDe ? "2 Zimmer / Nacht · inklusive Frühstück" : "2 rooms / night · incl. breakfast"}
                  </div>
                  <LocalizedLink
                    to="/villa-reservation?villa=villa-frangipani"
                    className="block w-full text-center bg-accent-500 hover:bg-accent-100 text-background-50 hover:text-foreground-950 py-3 rounded-md font-medium whitespace-nowrap cursor-pointer transition-colors"
                  >
                    <i className="ri-calendar-check-line mr-2"></i>
                    {isDe ? "Jetzt buchen" : "Book now"}
                  </LocalizedLink>
                  <BookingCompanyNotice isDe={isDe} />
                </div>

                {/* Other villas */}
                <div className="bg-background-50 border border-background-200 rounded-lg p-6">
                  <h3 className="font-heading text-lg text-foreground-950 mb-4">
                    {isDe ? "Weitere Villen" : "More Villas"}
                  </h3>
                  <div className="space-y-4">
                    <LocalizedLink
                      to="/villa-bougainvillea"
                      className="flex items-center gap-3 group"
                    >
                      <img
                        src={img("villas/villa-bougainvillea/villa-bougainvillea-view-01.jpg")}
                        alt="Villa Bougainvillea"
                        className="w-14 h-14 rounded object-cover flex-shrink-0"
                      />
                      <div>
                        <div className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors">
                          Villa Bougainvillea
                        </div>
                        <div className="text-xs text-foreground-500">
                          {isDe ? "1 Zimmer · 78 qm" : "1 room · 78 sqm"}
                        </div>
                      </div>
                    </LocalizedLink>
                    <LocalizedLink
                      to="/villa-hibiscus"
                      className="flex items-center gap-3 group"
                    >
                      <img
                        src={img("villas/villa-hibiscus/villa-hibiscus-view-01.jpg")}
                        alt={isDe ? "Villa Hibiskus" : "Villa Hibiscus"}
                        className="w-14 h-14 rounded object-cover flex-shrink-0"
                      />
                      <div>
                        <div className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors">
                          {isDe ? "Villa Hibiskus" : "Villa Hibiscus"}
                        </div>
                        <div className="text-xs text-foreground-500">
                          {isDe ? "3 Zimmer · 230 qm" : "3 rooms · 230 sqm"}
                        </div>
                      </div>
                    </LocalizedLink>
                  </div>
                </div>

                {/* Contact */}
                <div className="bg-background-50 border border-background-200 rounded-lg p-6">
                  <h3 className="font-heading text-lg text-foreground-950 mb-4">
                    {isDe ? "Kontakt" : "Contact"}
                  </h3>
                  <div className="space-y-3 text-sm text-foreground-700">
                    <div className="flex items-center gap-2">
                      <i className="ri-phone-line text-accent-700"></i>
                      <span>+62 81 246 21 8000</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-mail-line text-accent-700"></i>
                      <span>office@balivwtour.com</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}